import { type Dtos, ErrorKey, type SuretyQuoteId, SuretyQuoteStatus } from "@inrev/common";
import { Option } from "@inrev/core";
import { hashKey, type QueryClient } from "@tanstack/react-query";
import { isApiError } from "../../utils/error";
import { bondRequestIssuanceQueryKey, bondRequestsQueryKey } from "./bond-request.keys";

export const redirectToIssuedBond = (error: Error): Option<string> =>
	isApiError(error) &&
	error.key === ErrorKey.bond_request_already_written &&
	Option.isSome(error.entityId)
		? `/bonds/${error.entityId}`
		: undefined;

export const invalidateBondRequestIfClosed =
	(queryClient: QueryClient, id: SuretyQuoteId) => (_data: unknown, error: Error | null) => {
		if (isApiError(error) && error.key === ErrorKey.bond_request_already_closed) {
			void queryClient.invalidateQueries({ queryKey: bondRequestsQueryKey(id) });
		}
	};

/**
 * Issuing deletes the issuance, so refetching it for a request that is now bound returns
 * `bond_request_already_written` and shows the issuer the redirect modal meant for stale pages.
 */
export const invalidateBondRequestAfterIssue = async (
	queryClient: QueryClient,
	id: SuretyQuoteId,
): Promise<void> => {
	const issuanceHash = hashKey(bondRequestIssuanceQueryKey(id));
	await queryClient.invalidateQueries({
		queryKey: bondRequestsQueryKey(id),
		predicate: (query) => query.queryHash !== issuanceHash,
	});
	const request = queryClient.getQueryData<
		Dtos.SuretyQuote.Get.Response | Dtos.Admin.Quote.Get.Response
	>(bondRequestsQueryKey(id));
	if (request?.status === SuretyQuoteStatus.bound) return;
	await queryClient.invalidateQueries({ queryKey: bondRequestIssuanceQueryKey(id) });
};
